import './styles/globalPageStyles';
import './init';
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';

import { isProd } from './utils/env';
import Login from './pages/Login/Login';
import SetupTwoFactor from './pages/SetupTwoFactor/SetupTwoFactor';
import ChallengeTwoFactor from './pages/ChallengeTwoFactor/ChallengeTwoFactor';
import Member from './pages/Member/Member';
import { useAuth } from './hooks/useAuth';
import FiltersProvider from './components/ReportFilters/Filters';
import { ForgotPassword } from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './components/ResetPassword/ResetPassword';
import LoadingSpinner from './components/UI/LoadingSpinner/LoadingSpinner';
import MainNavigation from './components/Layout/MainNavigation/MainNavigation';
import ActivateAccount from './pages/ActivateAccount/ActivateAccount';
import ConfirmAccount from './pages/ConfirmAccount/ConfirmAccount';
import { NotificationCategories } from './interfaces/notification.interface';

import { AvatarProvider } from './context/userData-context';
import { ThemeContext } from './context/theme-context';
import { SocketProvider } from './context/SocketContext';
import { AuthProvider } from './providers/auth.provider';
import { ErrorProvider } from './providers/error.provider';

import './components/UserReport/pagination.css';
import './components/ContentReports/ContentReports.css';
// Global stylesheets that Bulk / BulkTests / SingleReports tables rely on but do not import themselves.
// Kept at the app root so route-level code splitting cannot move them into another route's CSS chunk.
import './components/Table/ResponsiveTable.css';
import './components/UserReport/MainList.css';
import './pages/SingleReports/SingleReports.css';
import { RouteHistoryProvider } from './providers/routeHistory.provider';
import useCanExtractData from './hooks/useCanExtractData';
import useCanModifyEmbargoKeyword from './hooks/useCanModifyEmbargoKeyword';
import useCanModifyUrlExceptions from './hooks/useCanModifyUrlExceptions';
import useCanModifySkuChecker from './hooks/useCanModifySkuChecker';

const Bulk = React.lazy(() => import('./pages/Bulk/Bulk'));
const BulkTestsRoute = React.lazy(() => import('./routes/BulkTestsRoute'));
const SingleTestRoute = React.lazy(() => import('./routes/SingleTestRoute'));
const MyProfilePage = React.lazy(() => import('./pages/MyProfilePage/MyProfilePage'));
const StartPage = React.lazy(() => import('./pages/StartPage/StartPage'));
const AdminUsers = React.lazy(() => import('./pages/Admin/AdminUsers/AdminUsers'));
const AdminGroups = React.lazy(() => import('./pages/Admin/AdminGroups/AdminGroups'));
const SingleReports = React.lazy(() => import('./pages/SingleReports/SingleReports'));
const AdminBillings = React.lazy(() => import('./pages/Admin/AdminBillings/AdminBillings'));
const AdminDashboard = React.lazy(() => import('./pages/Admin/AdminDashboard/AdminDashboard'));
const AdminUserPage = React.lazy(() => import('./pages/Admin/AdminUsers/AdminUserPage/AdminUserPage'));
const AdminGroupBillings = React.lazy(
  () => import('./pages/Admin/AdminBillings/AdminGroupBillings/AdminGroupBillings')
);
const AllNotificationList = React.lazy(() => import('./pages/Notifications/AllNotificationList'));
const MaintenanceNotificationList = React.lazy(() => import('./pages/Notifications/MaintenanceNotificationList'));
const ReleaseNotificationList = React.lazy(() => import('./pages/Notifications/ReleaseNotificationList'));
const GeneralInfoNotificationList = React.lazy(() => import('./pages/Notifications/GeneralInfoNotificationList'));
const NotificationDisplay = React.lazy(() => import('./pages/NotificationDisplay'));
const TasksRunPage = React.lazy(() => import('./pages/Automation/TasksRunPage'));
const TaskStatisticsPage = React.lazy(() => import('./pages/Automation/TaskStatisticsPage'));
const HealthDashboard = React.lazy(() => import('./pages/Health/HealthDashboard/HealthDashboard'));
const AdminActivityLogs = React.lazy(() => import('./pages/Admin/AdminActivityLogs/AdminActivityLogs'));
const RunTestBulkPage = React.lazy(() => import('./pages/RunTestBulk/RunTestBulkPage'));
const AllTaskRunsPage = React.lazy(() => import('./pages/Automation/AllTaskRunsPage'));
const Documentation = React.lazy(() => import('./pages/Documentation/Documentation'));
const BannedUserNotificationList = React.lazy(() => import('./pages/Notifications/BannedUserNotifications'));
const ContentQA = React.lazy(() => import('./pages/ContentQA/ContentQA').then((m) => ({ default: m.ContentQA })));
const AdminGroupPage = React.lazy(() =>
  import('./pages/Admin/AdminGroups/AdminGroupPage/AdminGroupPage').then((m) => ({ default: m.AdminGroupPage }))
);
const DocumentationPublic = React.lazy(() =>
  import('./pages/Documentation/DocumentationPublic').then((m) => ({ default: m.DocumentationPublic }))
);

const env = import.meta.env.VITE_ENV;

if (env === 'staging') import('./styles/env-staging.css');
if (env === 'dev') import('./styles/env-dev.css');

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const { isAdmin, isSuperAdmin, isGroupAdmin, isAuthenticated, group, userId } = useAuth();
  const canExtractData = useCanExtractData();
  const canModifyEmbargoKeyword = useCanModifyEmbargoKeyword();
  const canModifyUrlExceptions = useCanModifyUrlExceptions();
  const canModifySkuChecker = useCanModifySkuChecker();

  const routes = isAuthenticated ? (
    <main
      style={{
        marginLeft: isSidebarOpen ? '240px' : '75px',
        width: isSidebarOpen ? 'calc(100% - 240px)' : 'calc(100% - 75px)',
        paddingLeft: '0px'
      }}
    >
      <React.Suspense fallback={<LoadingSpinner asOverlay />}>
        <SnackbarProvider
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom'
          }}
        />
        <AuthProvider>
          <SocketProvider>
            <Routes>
              <Route path="/" element={<Navigate to={isProd ? '/search' : '/content-qa'} replace />} />
              {!isProd && <Route path="/content-qa" element={<ContentQA />} />}
              <Route path="/documentation/:page" element={<Documentation />} />
              <Route path="/search" element={<SingleReports />} />
              <Route path="/bulk" element={<Bulk />} />
              <Route path="/bulk/:id" element={<BulkTestsRoute isAuthenticated={isAuthenticated} />} />
              <Route path="/confirm-account/:token" element={<ConfirmAccount />} />
              <Route path="/my-profile" element={<MyProfilePage />} />
              <Route path="/report/:id" element={<SingleTestRoute isAuthenticated={isAuthenticated} />} />

              {group?.runTask ? (
                <>
                  <Route path="/automations/all" element={<AllTaskRunsPage />} />
                  <Route path="/automations/tasks/:id" element={<TasksRunPage />} />
                </>
              ) : null}

              <Route path="/notifications/all" element={<AllNotificationList />} />
              <Route
                path={`/notifications/${NotificationCategories.MaintenanceNotice}`}
                element={<MaintenanceNotificationList />}
              />
              <Route
                path={`/notifications/${NotificationCategories.ReleaseNotes}`}
                element={<ReleaseNotificationList />}
              />
              <Route
                path={`/notifications/${NotificationCategories.GeneralInformation}`}
                element={<GeneralInfoNotificationList />}
              />
              <Route
                path={`/notifications/${NotificationCategories.BannedUser}`}
                element={<BannedUserNotificationList />}
              />
              <Route path="/notifications/view/:id" element={<NotificationDisplay />} />

              {(isSuperAdmin ||
                canExtractData ||
                canModifyEmbargoKeyword ||
                canModifyUrlExceptions ||
                canModifySkuChecker) && <Route path="/admin/dashboard" element={<AdminDashboard />} />}
              {isSuperAdmin && (
                <>
                  <Route path="/admin/health" element={<HealthDashboard />} />
                  <Route path="/admin/logs" element={<AdminActivityLogs />} />
                  <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                </>
              )}

              {isSuperAdmin && group?.runTask && (
                <Route path="/admin/taskStatistics" element={<TaskStatisticsPage />} />
              )}

              {isAdmin && (
                <>
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/users/:id" element={<AdminUserPage />} />
                </>
              )}
              {!isProd && isSuperAdmin && (
                <>
                  <Route path="/admin/billings" element={<AdminBillings />} />
                  <Route path="/admin/billings/:id" element={<AdminGroupBillings />} />
                </>
              )}
              {(isSuperAdmin || isGroupAdmin) && (
                <>
                  <Route path="/admin/groups" element={<AdminGroups />} />
                  <Route path="/admin/group/:id" element={<AdminGroupPage />} />
                </>
              )}
              <Route path="/runTestBulk" element={<RunTestBulkPage />} />
              <Route path="/*" element={userId ? <Navigate to="/" replace /> : null} />
            </Routes>
          </SocketProvider>
        </AuthProvider>
      </React.Suspense>
    </main>
  ) : (
    <main>
      <React.Suspense fallback={<LoadingSpinner asOverlay />}>
        <Routes>
          <Route path="/bulk/:id" element={<BulkTestsRoute isAuthenticated={isAuthenticated} />} />
          <Route path="/report/:id" element={<SingleTestRoute isAuthenticated={isAuthenticated} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/2fa/setup" element={<SetupTwoFactor />} />
          <Route path="/2fa/challenge" element={<ChallengeTwoFactor />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route path="/activate/:activateToken" element={<Member />} />
          <Route path="/resetPassword/:token" element={<ResetPassword />} />
          <Route path="/" element={<StartPage />} />
          <Route path="/activateAccount/:token" element={<ActivateAccount />} />
          <Route path="/confirm-account/:token" element={<ConfirmAccount />} />
          <Route path="/*" element={<Login />} />
          <Route path="/runTestBulk" element={<RunTestBulkPage />} />
          <Route path="/documentation/PreQA/:token" element={<DocumentationPublic page="PreQA" />} />
        </Routes>
      </React.Suspense>
    </main>
  );

  return (
    <ErrorProvider>
      <AvatarProvider>
        <FiltersProvider>
          <Router useTransitions={false}>
            <ThemeContext>
              <RouteHistoryProvider>
                <MainNavigation setIsSidebarOpen={setIsSidebarOpen} isSidebarOpen={isSidebarOpen} />
                {routes}
              </RouteHistoryProvider>
            </ThemeContext>
          </Router>
        </FiltersProvider>
      </AvatarProvider>
    </ErrorProvider>
  );
};

export default App;
